import {
  Box,
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useRef, useState } from "react";
import type { ElementType, KeyboardEvent as ReactKeyboardEvent } from "react";

const MENU_LIST_SHADOW = "0 8px 32px rgba(0,0,0,0.12)";
const ONFLY_BLUE = "#009EFB";

export interface NavSubItem {
  name: string;
  icon?: ElementType;
  action?: () => void;
  path?: string;
  subitems?: NavSubItem[];
}

interface GGMenuItemProps {
  name: string;
  icon?: ElementType;
  subitems?: NavSubItem[];
  action?: () => void;
  activePath?: string;
}

/** Item ativo se path bate exatamente ou é prefixo de sub-rota. */
const isItemActive = (path: string | undefined, activePath: string | undefined): boolean => {
  if (!path || !activePath) return false;
  return activePath === path || activePath.startsWith(path + "/");
};

const hasActiveDescendant = (items: NavSubItem[] | undefined, activePath: string | undefined): boolean => {
  if (!items) return false;
  return items.some((it) => isItemActive(it.path, activePath) || hasActiveDescendant(it.subitems, activePath));
};

export const GGMenuItem = ({ name, icon, subitems, action, activePath }: GGMenuItemProps) => {
  const textColor = useColorModeValue("gray.700", "white");
  const activeColor = ONFLY_BLUE;
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const activeBg = useColorModeValue("rgba(0,158,251,0.10)", "rgba(0,158,251,0.14)");
  const chevronColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const menuListBg = useColorModeValue("white", "#0f1f3a");
  const menuListBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  // Sem subitems → botão real (focável por teclado, com aria-current).
  if (!subitems || subitems.length === 0) {
    const active = isItemActive(undefined, activePath) || false; // item top-level sem path não marca ativo
    return (
      <Box
        as="button"
        type="button"
        p={3}
        borderRadius="md"
        w="100%"
        textAlign="left"
        cursor="pointer"
        border="none"
        bg={active ? activeBg : "transparent"}
        color={active ? activeColor : textColor}
        _hover={{ bg: hoverBg, color: activeColor }}
        _focusVisible={{ outline: "2px solid", outlineColor: ONFLY_BLUE, outlineOffset: 2 }}
        onClick={action}
        aria-current={active ? "page" : undefined}
      >
        <Flex alignItems="center">
          <Icon as={icon} color="ggBlue.300" boxSize={4} aria-hidden />
          <Text pl={2} fontSize={16} as="b">
            {name}
          </Text>
        </Flex>
      </Box>
    );
  }

  const sectionActive = hasActiveDescendant(subitems, activePath);

  return (
    <Menu isLazy gutter={0}>
      {({ isOpen }) => (
        <>
          <MenuButton
            p={3}
            borderRadius="md"
            _hover={{ bg: hoverBg }}
            _expanded={{ bg: hoverBg, color: activeColor }}
            _focusVisible={{ outline: "2px solid", outlineColor: ONFLY_BLUE, outlineOffset: 2 }}
            w="100%"
            textAlign="left"
            color={sectionActive ? activeColor : textColor}
            aria-haspopup="menu"
            aria-expanded={isOpen}
          >
            <Flex alignItems="center" justify="space-between">
              <Flex alignItems="center" flex={1}>
                <Icon as={icon} color="ggBlue.300" boxSize={4} aria-hidden />
                <Text pl={2} fontSize={16} as="b">
                  {name}
                </Text>
              </Flex>
              <Icon
                as={isOpen ? ChevronDownIcon : ChevronRightIcon}
                color={chevronColor}
                boxSize={5}
                aria-hidden
                transition="transform 0.2s"
              />
            </Flex>
          </MenuButton>

          <MenuList
            bg={menuListBg}
            borderColor={menuListBorder}
            py={1}
            boxShadow={MENU_LIST_SHADOW}
            minW="220px"
          >
            {subitems.map((subitem) =>
              subitem.subitems && subitem.subitems.length > 0 ? (
                <NestedSubmenu
                  key={subitem.name}
                  subitem={subitem}
                  activePath={activePath}
                  menuListBg={menuListBg}
                  menuListBorder={menuListBorder}
                  hoverBg={hoverBg}
                  textColor={textColor}
                  activeBg={activeBg}
                  activeColor={activeColor}
                />
              ) : (
                <LeafMenuItem
                  key={subitem.name}
                  subitem={subitem}
                  activePath={activePath}
                  hoverBg={hoverBg}
                  textColor={textColor}
                  activeBg={activeBg}
                  activeColor={activeColor}
                />
              )
            )}
          </MenuList>
        </>
      )}
    </Menu>
  );
};

interface SubStyleProps {
  menuListBg: string;
  menuListBorder: string;
  hoverBg: string;
  textColor: string;
  activeBg: string;
  activeColor: string;
}

/** Item folha (sem sub-submenu) — MenuItem Chakra com destaque de ativo. */
const LeafMenuItem = ({
  subitem, activePath, hoverBg, textColor, activeBg, activeColor,
}: { subitem: NavSubItem; activePath?: string } & Pick<SubStyleProps, "hoverBg" | "textColor" | "activeBg" | "activeColor">) => {
  const active = isItemActive(subitem.path, activePath);
  return (
    <MenuItem
      onClick={subitem.action}
      bg={active ? activeBg : "transparent"}
      color={active ? activeColor : textColor}
      _hover={{ bg: hoverBg, color: activeColor }}
      _focusVisible={{ outline: "2px solid", outlineColor: ONFLY_BLUE, outlineOffset: 2 }}
      aria-current={active ? "page" : undefined}
    >
      {subitem.icon && <Icon mr={2} as={subitem.icon} color="ggBlue.200" boxSize={3.5} aria-hidden />}
      <Text fontWeight={active ? "700" : "500"} fontSize="sm">
        {subitem.name}
      </Text>
    </MenuItem>
  );
};

/** Submenu aninhado (3º nível). Renderiza uma MenuList absoluta ao lado do
 *  item pai, controlada por estado — abre por hover (desktop, com delay de
 *  fechamento pra ponte estável) e por teclado (Enter/→ abre, Esc/← fecha).
 *  ARIA: role=menu/menuitem, aria-haspopup, aria-expanded. */
const NestedSubmenu = ({
  subitem, activePath, menuListBg, menuListBorder, hoverBg, textColor, activeBg, activeColor,
}: { subitem: NavSubItem; activePath?: string } & SubStyleProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subListRef = useRef<HTMLDivElement | null>(null);
  const branchActive = isItemActive(subitem.path, activePath) || hasActiveDescendant(subitem.subitems, activePath);

  const open = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setIsOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsOpen(false), 120);
  };
  const close = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setIsOpen(false);
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "Enter" || e.key === "ArrowRight" || e.key === " ") {
      e.preventDefault();
      open();
      // Move o foco pro primeiro item do submenu.
      requestAnimationFrame(() => {
        const first = subListRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
        first?.focus();
      });
    } else if (e.key === "ArrowLeft" || e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  return (
    <Box
      position="relative"
      role="none"
      onMouseEnter={open}
      onMouseLeave={scheduleClose}
    >
      <MenuItem
        bg={branchActive ? activeBg : "transparent"}
        color={branchActive ? activeColor : textColor}
        _hover={{ bg: hoverBg, color: activeColor }}
        _focusVisible={{ outline: "2px solid", outlineColor: ONFLY_BLUE, outlineOffset: 2 }}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <Flex alignItems="center" pr={2}>
          {subitem.icon && <Icon mr={2} as={subitem.icon} color="ggBlue.200" boxSize={3.5} aria-hidden />}
          <Text flex={1} fontWeight={branchActive ? "700" : "500"} fontSize="sm">
            {subitem.name}
          </Text>
          <ChevronRightIcon aria-hidden />
        </Flex>
      </MenuItem>

      {isOpen && (
        <MenuList
          ref={subListRef}
          bg={menuListBg}
          borderColor={menuListBorder}
          position="absolute"
          top="0"
          left="100%"
          zIndex={999}
          py={1}
          minW="200px"
          boxShadow={MENU_LIST_SHADOW}
          role="menu"
          onMouseEnter={open}
          onMouseLeave={scheduleClose}
        >
          {subitem.subitems!.map((subSub) => (
            <LeafMenuItem
              key={subSub.name}
              subitem={subSub}
              activePath={activePath}
              hoverBg={hoverBg}
              textColor={textColor}
              activeBg={activeBg}
              activeColor={activeColor}
            />
          ))}
        </MenuList>
      )}
    </Box>
  );
};